import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { ICashFlow } from "../../../interface/cashFlow";

interface PaymentRow {
  id: number;
  dateReceived: string;
  dateFrom: string;
  dateTo: string;
  amount?: number;
  notes: string;
  invalidFields?: string[];
  isNewRow?: boolean;
  address?: string;
  group?: string;
}

export interface ILoanPaymentRow {
  id: number;
  date: string;
  loan: string;
  amount?: number;
  notes: string;
  type: "Principal" | "Interest";
  invalidFields?: string[];
  isRowEditable: boolean;
  address?: string;
  group?: string;
  loanId?: string;
}

interface IExpenseRow {
  id: number;
  group: string;
  date: Date | null;
  amount: number | null;
  notes: string;
  isNew: boolean;
  address: string;
  trackerId: number;
}

interface IPurchaseCostRow {
  id: number;
  group: string;
  date: Date | null;
  amount: number | null;
  mortgage: number;
  notes: string;
  isNew: boolean;
  address: string;
  trackerId: number;
  loanId?: string;
  loan: string;
}

interface ITaxDepreciationRow {
  id: number;
  group: string;
  date: Date | null;
  amount: number | null;
  notes: string;
  isNew: boolean;
  address: string;
  trackerId: number;
}

interface IPropertyEditData {
  rent: PaymentRow[];
  loanPrincipal: ILoanPaymentRow[];
  loanInterest: ILoanPaymentRow[];
  expenses: IExpenseRow[];
  purchaseCosts: IPurchaseCostRow[];
  taxDepreciation: ITaxDepreciationRow[];
}

interface ICashFlowContextType {
  cashflow: ICashFlow | undefined;
  setCashFlow: React.Dispatch<React.SetStateAction<ICashFlow | undefined>>;
  selectedProperty: string;
  setSelectedProperty: React.Dispatch<React.SetStateAction<string>>;

  // Property address management
  propertyAddressMap: Record<string, string>;
  setPropertyAddress: (propertyId: string, address: string) => void;

  // Common confirmation dialog
  showCommonConfirmationDialog: boolean;
  setShowCommonConfirmationDialog: React.Dispatch<React.SetStateAction<boolean>>;
  getAllDataToSave: () => Array<{
    type: string;
    address: string;
    group: string;
    date: string;
    amount: number | null;
    notes: string;
    portfolioTrackerId: string;
    LoanId?: string;
  }>;

  // Rent data
  editRentTableData: PaymentRow[];
  setEditRentTableData: React.Dispatch<React.SetStateAction<PaymentRow[]>>;
  getPropertyRentData: (propertyId: string) => PaymentRow[];
  setPropertyRentData: (propertyId: string, data: PaymentRow[]) => void;
  resetEditRentData: () => void;
  transferTempDataToProperty: (propertyId: string) => void;

  // Loan data
  editLoanPrincipalData: ILoanPaymentRow[];
  editLoanInterestData: ILoanPaymentRow[];
  setEditLoanPrincipalData: React.Dispatch<React.SetStateAction<ILoanPaymentRow[]>>;
  setEditLoanInterestData: React.Dispatch<React.SetStateAction<ILoanPaymentRow[]>>;
  getPropertyLoanData: (propertyId: string) => { principal: ILoanPaymentRow[]; interest: ILoanPaymentRow[] };
  setPropertyLoanData: (propertyId: string, principal: ILoanPaymentRow[], interest: ILoanPaymentRow[]) => void;
  resetEditLoanData: () => void;

  // Expense data
  editExpenseData: IExpenseRow[];
  setEditExpenseData: React.Dispatch<React.SetStateAction<IExpenseRow[]>>;
  getPropertyExpenseData: (propertyId: string) => IExpenseRow[];
  setPropertyExpenseData: (propertyId: string, data: IExpenseRow[]) => void;
  resetEditExpenseData: () => void;

  // Purchase cost data
  editPurchaseCostData: IPurchaseCostRow[];
  setEditPurchaseCostData: React.Dispatch<React.SetStateAction<IPurchaseCostRow[]>>;
  getPropertyPurchaseCostData: (propertyId: string) => IPurchaseCostRow[];
  setPropertyPurchaseCostData: (propertyId: string, data: IPurchaseCostRow[]) => void;
  resetEditPurchaseCostData: () => void;

  // Tax & depreciation data
  editTaxDepreciationData: ITaxDepreciationRow[];
  setEditTaxDepreciationData: React.Dispatch<React.SetStateAction<ITaxDepreciationRow[]>>;
  getPropertyTaxDepreciationData: (propertyId: string) => ITaxDepreciationRow[];
  setPropertyTaxDepreciationData: (propertyId: string, data: ITaxDepreciationRow[]) => void;
  resetEditTaxDepreciationData: () => void;

  // Global reset
  resetAllPropertyData: () => void;
}

const CashFlowContextType = createContext<ICashFlowContextType | undefined>(undefined);

export const CashFlowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cashflow, setCashFlow] = useState<ICashFlow | undefined>(undefined);
  const [propertyDataMap, setPropertyDataMap] = useState<Record<string, IPropertyEditData>>({});
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [showCommonConfirmationDialog, setShowCommonConfirmationDialog] = useState<boolean>(false);
  const [propertyAddressMap, setPropertyAddressMap] = useState<Record<string, string>>({});

  // Default data structures
  const defaultRentData: PaymentRow[] = [
    {
      id: 0,
      dateReceived: "",
      dateFrom: "",
      dateTo: "",
      amount: undefined,
      notes: "",
      invalidFields: [],
      isNewRow: true,
    },
  ];

  const defaultLoanPrincipalData: ILoanPaymentRow[] = [
    {
      id: 0,
      date: "",
      loan: "",
      amount: undefined,
      notes: "",
      type: "Principal",
      invalidFields: [],
      isRowEditable: true,
    },
  ];

  const defaultLoanInterestData: ILoanPaymentRow[] = [
    {
      id: 0,
      date: "",
      loan: "",
      amount: undefined,
      notes: "",
      type: "Interest",
      invalidFields: [],
      isRowEditable: true,
    },
  ];

  const defaultExpenseData: IExpenseRow[] = [
    {
      id: 1,
      group: "Property management",
      date: null,
      amount: null,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
    },
    { id: 2, group: "Service contracts", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 3, group: "Rates", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 4, group: "Body corporate", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 5, group: "Insurance", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 6, group: "Repairs", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 7, group: "Land lease", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 8, group: "Gardening", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 9, group: "Cleaning", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 10, group: "Other", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
  ];

  const defaultPurchaseCostData: IPurchaseCostRow[] = [
    {
      id: 1,
      group: "Capital Improvements at purchase",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loan: "",
    },
    {
      id: 2,
      group: "Solicitors conveyancing costs",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loan: "",
    },
    {
      id: 3,
      group: "Stamp duty",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loan: "",
    },
    {
      id: 4,
      group: "Other purchase expenses",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loan: "",
    },
    {
      id: 5,
      group: "Loan establishment fees",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loanId: 0,
      loan: "",
    },
    {
      id: 6,
      group: "Valuation fees",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loanId: 0,
      loan: "",
    },
    {
      id: 7,
      group: "Other loan costs",
      date: null,
      amount: null,
      mortgage: 0,
      notes: "",
      isNew: false,
      address: "",
      trackerId: 0,
      loanId: 0,
      loan: "",
    },
  ];

  const defaultTaxDepreciationData: ITaxDepreciationRow[] = [
    { id: 1, group: "Depreciation", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
    { id: 2, group: "Land tax", date: null, amount: null, notes: "", isNew: false, address: "", trackerId: 0 },
  ];

  // Get current property data
  const getPropertyData = (propertyId: string): IPropertyEditData => {
    if (propertyId === "_temp") {
      return (
        propertyDataMap["_temp"] || {
          rent: defaultRentData,
          loanPrincipal: defaultLoanPrincipalData,
          loanInterest: defaultLoanInterestData,
          expenses: defaultExpenseData,
          purchaseCosts: defaultPurchaseCostData,
          taxDepreciation: defaultTaxDepreciationData,
        }
      );
    }

    return (
      propertyDataMap[propertyId] || {
        rent: defaultRentData,
        loanPrincipal: defaultLoanPrincipalData,
        loanInterest: defaultLoanInterestData,
        expenses: defaultExpenseData,
        purchaseCosts: defaultPurchaseCostData,
        taxDepreciation: defaultTaxDepreciationData,
      }
    );
  };

  // Current property data getters
  const editRentTableData = selectedProperty
    ? getPropertyData(selectedProperty).rent
    : propertyDataMap["_temp"]?.rent || defaultRentData;
  const editLoanPrincipalData = selectedProperty
    ? getPropertyData(selectedProperty).loanPrincipal
    : propertyDataMap["_temp"]?.loanPrincipal || defaultLoanPrincipalData;
  const editLoanInterestData = selectedProperty
    ? getPropertyData(selectedProperty).loanInterest
    : propertyDataMap["_temp"]?.loanInterest || defaultLoanInterestData;
  const editExpenseData = selectedProperty
    ? getPropertyData(selectedProperty).expenses
    : propertyDataMap["_temp"]?.expenses || defaultExpenseData;
  const editPurchaseCostData = selectedProperty
    ? getPropertyData(selectedProperty).purchaseCosts
    : propertyDataMap["_temp"]?.purchaseCosts || defaultPurchaseCostData;
  const editTaxDepreciationData = selectedProperty
    ? getPropertyData(selectedProperty).taxDepreciation
    : propertyDataMap["_temp"]?.taxDepreciation || defaultTaxDepreciationData;

  // Data setters for current property
  const setEditRentTableData = (data: PaymentRow[] | ((prev: PaymentRow[]) => PaymentRow[])) => {
    if (!selectedProperty) {
      // Allow temporary updates even without a selected property
      // This enables users to enter data before selecting a property
      const tempData = typeof data === "function" ? data(defaultRentData) : data;
      // Store in a temporary property key until a property is selected
      setPropertyDataMap((prev) => ({
        ...prev,
        _temp: {
          rent: tempData,
          loanPrincipal: defaultLoanPrincipalData,
          loanInterest: defaultLoanInterestData,
          expenses: defaultExpenseData,
          purchaseCosts: defaultPurchaseCostData,
          taxDepreciation: defaultTaxDepreciationData,
        },
      }));
      return;
    }

    setPropertyDataMap((prev) => {
      const currentPropertyData = getPropertyData(selectedProperty);
      const currentData = currentPropertyData.rent;
      const newData = typeof data === "function" ? data(currentData) : data;

      // If we have temp data and are now selecting a property, merge the temp data
      const tempData = prev["_temp"]?.rent;
      const finalData = tempData && currentData === defaultRentData ? tempData : newData;

      // Remove temp data when property is selected
      const { _temp, ...restData } = prev;

      return {
        ...restData,
        [selectedProperty]: {
          ...currentPropertyData,
          rent: finalData,
        },
      };
    });
  };

  const setEditLoanPrincipalData = (data: ILoanPaymentRow[] | ((prev: ILoanPaymentRow[]) => ILoanPaymentRow[])) => {
    if (!selectedProperty) {
      // Allow temporary updates even without a selected property
      const tempData = typeof data === "function" ? data(defaultLoanPrincipalData) : data;
      setPropertyDataMap((prev) => ({
        ...prev,
        _temp: {
          ...(prev["_temp"] || {
            rent: defaultRentData,
            loanPrincipal: defaultLoanPrincipalData,
            loanInterest: defaultLoanInterestData,
            expenses: defaultExpenseData,
            purchaseCosts: defaultPurchaseCostData,
            taxDepreciation: defaultTaxDepreciationData,
          }),
          loanPrincipal: tempData,
        },
      }));
      return;
    }

    setPropertyDataMap((prev) => {
      const currentPropertyData = getPropertyData(selectedProperty);
      const currentData = currentPropertyData.loanPrincipal;
      const newData = typeof data === "function" ? data(currentData) : data;

      // If we have temp data and are now selecting a property, merge the temp data
      const tempData = prev["_temp"]?.loanPrincipal;
      const finalData = tempData && currentData === defaultLoanPrincipalData ? tempData : newData;

      // Remove temp data when property is selected
      const { _temp, ...restData } = prev;

      return {
        ...restData,
        [selectedProperty]: {
          ...currentPropertyData,
          loanPrincipal: finalData,
        },
      };
    });
  };

  const setEditLoanInterestData = (data: ILoanPaymentRow[] | ((prev: ILoanPaymentRow[]) => ILoanPaymentRow[])) => {
    if (!selectedProperty) {
      // Allow temporary updates even without a selected property
      const tempData = typeof data === "function" ? data(defaultLoanInterestData) : data;
      setPropertyDataMap((prev) => ({
        ...prev,
        _temp: {
          ...(prev["_temp"] || {
            rent: defaultRentData,
            loanPrincipal: defaultLoanPrincipalData,
            loanInterest: defaultLoanInterestData,
            expenses: defaultExpenseData,
            purchaseCosts: defaultPurchaseCostData,
            taxDepreciation: defaultTaxDepreciationData,
          }),
          loanInterest: tempData,
        },
      }));
      return;
    }

    setPropertyDataMap((prev) => {
      const currentPropertyData = getPropertyData(selectedProperty);
      const currentData = currentPropertyData.loanInterest;
      const newData = typeof data === "function" ? data(currentData) : data;

      // If we have temp data and are now selecting a property, merge the temp data
      const tempData = prev["_temp"]?.loanInterest;
      const finalData = tempData && currentData === defaultLoanInterestData ? tempData : newData;

      // Remove temp data when property is selected
      const { _temp, ...restData } = prev;

      return {
        ...restData,
        [selectedProperty]: {
          ...currentPropertyData,
          loanInterest: finalData,
        },
      };
    });
  };

  const setEditExpenseData = (data: IExpenseRow[] | ((prev: IExpenseRow[]) => IExpenseRow[])) => {
    if (!selectedProperty) {
      // Allow temporary updates even without a selected property
      const tempData = typeof data === "function" ? data(defaultExpenseData) : data;
      setPropertyDataMap((prev) => ({
        ...prev,
        _temp: {
          ...(prev["_temp"] || {
            rent: defaultRentData,
            loanPrincipal: defaultLoanPrincipalData,
            loanInterest: defaultLoanInterestData,
            expenses: defaultExpenseData,
            purchaseCosts: defaultPurchaseCostData,
            taxDepreciation: defaultTaxDepreciationData,
          }),
          expenses: tempData,
        },
      }));
      return;
    }

    setPropertyDataMap((prev) => {
      const currentPropertyData = getPropertyData(selectedProperty);
      const currentData = currentPropertyData.expenses;
      const newData = typeof data === "function" ? data(currentData) : data;

      // If we have temp data and are now selecting a property, merge the temp data
      const tempData = prev["_temp"]?.expenses;
      const finalData = tempData && currentData === defaultExpenseData ? tempData : newData;

      // Remove temp data when property is selected
      const { _temp, ...restData } = prev;

      return {
        ...restData,
        [selectedProperty]: {
          ...currentPropertyData,
          expenses: finalData,
        },
      };
    });
  };

  const setEditPurchaseCostData = (data: IPurchaseCostRow[] | ((prev: IPurchaseCostRow[]) => IPurchaseCostRow[])) => {
    if (!selectedProperty) {
      // Allow temporary updates even without a selected property
      const tempData = typeof data === "function" ? data(defaultPurchaseCostData) : data;
      setPropertyDataMap((prev) => ({
        ...prev,
        _temp: {
          ...(prev["_temp"] || {
            rent: defaultRentData,
            loanPrincipal: defaultLoanPrincipalData,
            loanInterest: defaultLoanInterestData,
            expenses: defaultExpenseData,
            purchaseCosts: defaultPurchaseCostData,
            taxDepreciation: defaultTaxDepreciationData,
          }),
          purchaseCosts: tempData,
        },
      }));
      return;
    }

    setPropertyDataMap((prev) => {
      const currentPropertyData = getPropertyData(selectedProperty);
      const currentData = currentPropertyData.purchaseCosts;
      const newData = typeof data === "function" ? data(currentData) : data;

      // If we have temp data and are now selecting a property, merge the temp data
      const tempData = prev["_temp"]?.purchaseCosts;
      const finalData = tempData && currentData === defaultPurchaseCostData ? tempData : newData;

      // Remove temp data when property is selected
      const { _temp, ...restData } = prev;

      return {
        ...restData,
        [selectedProperty]: {
          ...currentPropertyData,
          purchaseCosts: finalData,
        },
      };
    });
  };

  const setEditTaxDepreciationData = (
    data: ITaxDepreciationRow[] | ((prev: ITaxDepreciationRow[]) => ITaxDepreciationRow[])
  ) => {
    if (!selectedProperty) {
      // Allow temporary updates even without a selected property
      const tempData = typeof data === "function" ? data(defaultTaxDepreciationData) : data;
      setPropertyDataMap((prev) => ({
        ...prev,
        _temp: {
          ...(prev["_temp"] || {
            rent: defaultRentData,
            loanPrincipal: defaultLoanPrincipalData,
            loanInterest: defaultLoanInterestData,
            expenses: defaultExpenseData,
            purchaseCosts: defaultPurchaseCostData,
            taxDepreciation: defaultTaxDepreciationData,
          }),
          taxDepreciation: tempData,
        },
      }));
      return;
    }

    setPropertyDataMap((prev) => {
      const currentPropertyData = getPropertyData(selectedProperty);
      const currentData = currentPropertyData.taxDepreciation;
      const newData = typeof data === "function" ? data(currentData) : data;

      // If we have temp data and are now selecting a property, merge the temp data
      const tempData = prev["_temp"]?.taxDepreciation;
      const finalData = tempData && currentData === defaultTaxDepreciationData ? tempData : newData;

      // Remove temp data when property is selected
      const { _temp, ...restData } = prev;

      return {
        ...restData,
        [selectedProperty]: {
          ...currentPropertyData,
          taxDepreciation: finalData,
        },
      };
    });
  };

  // Property-specific getters
  const getPropertyRentData = (propertyId: string): PaymentRow[] => {
    return getPropertyData(propertyId).rent;
  };

  const getPropertyLoanData = (propertyId: string): { principal: ILoanPaymentRow[]; interest: ILoanPaymentRow[] } => {
    const data = getPropertyData(propertyId);
    return { principal: data.loanPrincipal, interest: data.loanInterest };
  };

  const getPropertyExpenseData = (propertyId: string): IExpenseRow[] => {
    return getPropertyData(propertyId).expenses;
  };

  const getPropertyPurchaseCostData = (propertyId: string): IPurchaseCostRow[] => {
    return getPropertyData(propertyId).purchaseCosts;
  };

  const getPropertyTaxDepreciationData = (propertyId: string): ITaxDepreciationRow[] => {
    return getPropertyData(propertyId).taxDepreciation;
  };

  // Property-specific setters
  const setPropertyRentData = (propertyId: string, data: PaymentRow[]) => {
    setPropertyDataMap((prev) => ({
      ...prev,
      [propertyId]: {
        ...getPropertyData(propertyId),
        rent: data,
      },
    }));
  };

  const setPropertyLoanData = (propertyId: string, principal: ILoanPaymentRow[], interest: ILoanPaymentRow[]) => {
    setPropertyDataMap((prev) => ({
      ...prev,
      [propertyId]: {
        ...getPropertyData(propertyId),
        loanPrincipal: principal,
        loanInterest: interest,
      },
    }));
  };

  const setPropertyExpenseData = (propertyId: string, data: IExpenseRow[]) => {
    setPropertyDataMap((prev) => ({
      ...prev,
      [propertyId]: {
        ...getPropertyData(propertyId),
        expenses: data,
      },
    }));
  };

  const setPropertyPurchaseCostData = (propertyId: string, data: IPurchaseCostRow[]) => {
    setPropertyDataMap((prev) => ({
      ...prev,
      [propertyId]: {
        ...getPropertyData(propertyId),
        purchaseCosts: data,
      },
    }));
  };

  const setPropertyTaxDepreciationData = (propertyId: string, data: ITaxDepreciationRow[]) => {
    setPropertyDataMap((prev) => ({
      ...prev,
      [propertyId]: {
        ...getPropertyData(propertyId),
        taxDepreciation: data,
      },
    }));
  };

  // Reset functions
  const resetEditRentData = () => {
    if (selectedProperty) {
      setPropertyDataMap((prev) => ({
        ...prev,
        [selectedProperty]: {
          ...getPropertyData(selectedProperty),
          rent: defaultRentData,
        },
      }));
    }
  };

  const resetEditLoanData = () => {
    if (selectedProperty) {
      setPropertyDataMap((prev) => ({
        ...prev,
        [selectedProperty]: {
          ...getPropertyData(selectedProperty),
          loanPrincipal: defaultLoanPrincipalData,
          loanInterest: defaultLoanInterestData,
        },
      }));
    }
  };

  const resetEditExpenseData = () => {
    if (selectedProperty) {
      setPropertyDataMap((prev) => ({
        ...prev,
        [selectedProperty]: {
          ...getPropertyData(selectedProperty),
          expenses: defaultExpenseData,
        },
      }));
    }
  };

  const resetEditPurchaseCostData = () => {
    if (selectedProperty) {
      setPropertyDataMap((prev) => ({
        ...prev,
        [selectedProperty]: {
          ...getPropertyData(selectedProperty),
          purchaseCosts: defaultPurchaseCostData,
        },
      }));
    }
  };

  const resetEditTaxDepreciationData = () => {
    if (selectedProperty) {
      setPropertyDataMap((prev) => ({
        ...prev,
        [selectedProperty]: {
          ...getPropertyData(selectedProperty),
          taxDepreciation: defaultTaxDepreciationData,
        },
      }));
    }
  };

  // Function to set property address
  const setPropertyAddress = useCallback((propertyId: string, address: string) => {
    setPropertyAddressMap((prev) => ({
      ...prev,
      [propertyId]: address,
    }));
  }, []);

  const resetAllPropertyData = useCallback(() => {
    setPropertyDataMap({});
    setSelectedProperty("");
  }, []);

  // Function to transfer temporary data to a selected property
  const transferTempDataToProperty = useCallback((propertyId: string) => {
    setPropertyDataMap((prev) => {
      const tempData = prev["_temp"];
      if (!tempData) return prev;

      const { _temp, ...restData } = prev;
      const existingPropertyData = getPropertyData(propertyId);

      return {
        ...restData,
        [propertyId]: {
          rent: tempData.rent || existingPropertyData.rent,
          loanPrincipal: tempData.loanPrincipal || existingPropertyData.loanPrincipal,
          loanInterest: tempData.loanInterest || existingPropertyData.loanInterest,
          expenses: tempData.expenses || existingPropertyData.expenses,
          purchaseCosts: tempData.purchaseCosts || existingPropertyData.purchaseCosts,
          taxDepreciation: tempData.taxDepreciation || existingPropertyData.taxDepreciation,
        },
      };
    });
  }, []); // Get all data that needs to be saved across all edit components
  const getAllDataToSave = () => {
    const allData: Array<{
      type: string;
      address: string;
      group: string;
      date: string;
      amount: number | null;
      notes: string;
      portfolioTrackerId: string;
      LoanId: string;
    }> = [];

    // Get property address from cashflow data or address map
    const getPropertyAddress = (propertyId: string) => {
      // First try to get from the stored address map
      if (propertyAddressMap[propertyId]) {
        return propertyAddressMap[propertyId];
      }

      // Fallback to cashflow data structure
      if (!cashflow) return "";
      const property = cashflow.PropertyDetails?.find(
        (p) => p.PropertyDetail.PortfolioTrackerId.toString() === propertyId
      );
      if (!property) return "";
      return [
        property.PropertyDetail.StreetAddress,
        property.PropertyDetail.Suburb,
        property.PropertyDetail.State,
        property.PropertyDetail.Postcode,
      ]
        .filter(Boolean)
        .join(", ");
    };

    // Process all properties with data
    Object.entries(propertyDataMap).forEach(([propertyId, propertyData]) => {
      const address = getPropertyAddress(propertyId);

      // Add rent data (only rows with id > 0)
      propertyData.rent
        .filter((row) => row.id > 0)
        .forEach((row) => {
          allData.push({
            type: "Rent",
            address,
            group: "Rental income",
            date: row.dateReceived,
            amount: row.amount || null,
            notes: row.notes,
            portfolioTrackerId: propertyId,
            LoanId: "0",
          });
        });

      // Add loan data (only rows with actual data)
      propertyData.loanPrincipal
        .filter((row) => row.id >= 0 && row.amount !== null && row.amount !== undefined && row.amount !== 0)
        .forEach((row) => {
          allData.push({
            type: "Loan",
            address,
            group: `${row.type} Payment`,
            date: row.date,
            amount: row.amount || null,
            notes: row.notes,
            portfolioTrackerId: propertyId,
            LoanId: row.loanId?.toString() || "0",
          });
        });

      propertyData.loanInterest
        .filter((row) => row.id >= 0 && row.amount !== null && row.amount !== undefined && row.amount !== 0)
        .forEach((row) => {
          allData.push({
            type: "Loan",
            address,
            group: `${row.type} Payment`,
            date: row.date,
            amount: row.amount || null,
            notes: row.notes,
            portfolioTrackerId: propertyId,
            LoanId: row.loanId?.toString() || "0",
          });
        }); // Add expense data (only new rows)
      propertyData.expenses
        .filter((row) => row.isNew)
        .forEach((row) => {
          allData.push({
            type: "Expense",
            address,
            group: row.group,
            date: row.date?.toISOString() || "",
            amount: row.amount,
            notes: row.notes,
            portfolioTrackerId: propertyId,
            LoanId: "0",
          });
        });

      // Add purchase cost data (only new rows)
      propertyData.purchaseCosts
        .filter((row) => row.isNew)
        .forEach((row) => {
          allData.push({
            type: "Purchase Cost",
            address,
            group: row.group,
            date: row.date?.toISOString() || "",
            amount: row.amount,
            notes: row.notes,
            portfolioTrackerId: propertyId,
            LoanId: row.mortgage.toString(),
          });
        });

      // Add tax & depreciation data (only new rows)
      propertyData.taxDepreciation
        .filter((row) => row.isNew)
        .forEach((row) => {
          allData.push({
            type: "Tax & Depreciation",
            address,
            group: row.group,
            date: row.date?.toISOString() || "",
            amount: row.amount,
            notes: row.notes,
            portfolioTrackerId: propertyId,
            LoanId: "0",
          });
        });
    });

    return allData;
  };

  return (
    <CashFlowContextType.Provider
      value={{
        cashflow,
        setCashFlow,
        selectedProperty,
        setSelectedProperty,

        // Property address management
        propertyAddressMap,
        setPropertyAddress,

        // Common confirmation dialog
        showCommonConfirmationDialog,
        setShowCommonConfirmationDialog,
        getAllDataToSave,

        // Rent data
        editRentTableData,
        setEditRentTableData,
        getPropertyRentData,
        setPropertyRentData,
        resetEditRentData,
        transferTempDataToProperty,

        // Loan data
        editLoanPrincipalData,
        editLoanInterestData,
        setEditLoanPrincipalData,
        setEditLoanInterestData,
        getPropertyLoanData,
        setPropertyLoanData,
        resetEditLoanData,

        // Expense data
        editExpenseData,
        setEditExpenseData,
        getPropertyExpenseData,
        setPropertyExpenseData,
        resetEditExpenseData,

        // Purchase cost data
        editPurchaseCostData,
        setEditPurchaseCostData,
        getPropertyPurchaseCostData,
        setPropertyPurchaseCostData,
        resetEditPurchaseCostData,

        // Tax & depreciation data
        editTaxDepreciationData,
        setEditTaxDepreciationData,
        getPropertyTaxDepreciationData,
        setPropertyTaxDepreciationData,
        resetEditTaxDepreciationData,

        // Global reset
        resetAllPropertyData,
      }}
    >
      {children}
    </CashFlowContextType.Provider>
  );
};

export const useCashFlowContext = () => {
  const context = useContext(CashFlowContextType);
  if (!context) {
    throw new Error("cashFlowContext must be used within a CashFlowProvider");
  }
  return context;
};
